import { TemplateResult } from 'lit-element';
import { EventEmitterMixin } from './EventEmitterMixin';

export const State = (() => {
	const _path = Symbol();
	const _href = Symbol();

	class State {
		constructor(path, href) {
			this.path = path;
			this.href = href;
		}
		get path() {
			return this[_path];
		}
		set path(v) {
			if(typeof v !== 'string')
				throw new TypeError('path must be a string.');
			this[_path] = v;
		}
		get href() {
			return this[_href];
		}
		set href(v) {
			if(typeof v !== 'string')
				throw new TypeError('href must be a string.');
			this[_href] = v;
		}
		toJSON() {
			return {
				path: this.path,
				href: this.href
			};
		}
		toString() {
			return JSON.stringify(this);
		}
		static fromObject(obj) {
			if(obj === null || typeof obj !== 'object')
				throw new TypeError('obj must be an object.');
			return new this(
				obj.path,
				obj.href
			);
		}
	}

	return State;
})();

export const Route = (() => {
	const _pathname = Symbol();
	const _template = Symbol();
	const _href = Symbol();
	const _title = Symbol();

	class Route {
		constructor(pathname, template, title) {
			this.pathname = pathname;
			this.template = template;
			this.href = null;
			this.title = title;
		}
		get pathname() {
			return this[_pathname];
		}
		set pathname(v) {
			if(typeof v !== 'string')
				throw new TypeError('pathname must be a string.');
			this[_pathname] = v;
		}
		get template() {
			return this[_template];
		}
		set template(v) {
			if(!(v instanceof TemplateResult))
				throw new TypeError('template must be an instance of TemplateResult.');
			this[_template] = v;
		}
		get href() {
			return this[_href];
		}
		set href(v) {
			if(v !== null && typeof v !== 'string')
				throw new TypeError('href must be a string.');
			this[_href] = v;
		}
		get title() {
			return this[_title];
		}
		set title(v) {
			if(typeof v !== 'string')
				throw new TypeError('title must be a string.');
			this[_title] = v;
		}
		static fromObject(obj) {
			if(obj === null || typeof obj !== 'object')
				throw new TypeError('obj must be an object.');
			
			return new this(
				obj.pathname || '',
				obj.template,
				obj.title || ''
			);
		}
	}

	return Route;
})();

export const Routes = (() => {
	const _items = Symbol();

	class Routes {
		constructor(...items) {
			this[_items] = [];

			this.add(...items);
		}
		//iterator protocol support
		*iterator() {
			for(let key in this[_items]) {
				let value = this[_items][key];
				yield value;
			}
		}
		//iterable protocol support
		[Symbol.iterator]() {
			return this.iterator();
		}
		get length() {
			return this[_items].length;
		}
		get(index) {
			return this[_items][index];
		}
		find(pathname) {
			if(typeof pathname !== 'string')
				throw new TypeError('pathname must be a string.');
			return this[_items].find((route) => route.pathname === pathname);
		}
		add(...items) {
			items.forEach((item, index) => {
				if(!(item instanceof Route))
					throw new TypeError(`argument index ${index} must be an instance of Route.`);
			});
			this[_items].push(...items);
		}
		static fromArray(arr) {
			if(!Array.isArray(arr))
				throw new TypeError('arr must be an array.');
			return new this(
				...arr.map(v => Route.fromObject(v))
			);
		}
	}

	return Routes;
})();

export const NO_STATE = 0;
export const PUSH_STATE = 1;
export const REPLACE_STATE = 2;
export const STATE_OPERATIONS = {
	NO_STATE,
	PUSH_STATE,
	REPLACE_STATE
};

export const Router = (() => {
	const _routes = Symbol();
	const _history = Symbol();
	const _document = Symbol();
	const _window = Symbol();
	const _titleAppend = Symbol();
	const _current = Symbol();
	const _popStateHandler = Symbol();
	const _clickHandler = Symbol();

	class Router extends EventEmitterMixin(class {}) {
		constructor(routes, history, document, window, titleAppend) {
			super();
			this[_clickHandler] = e => {
				const element = e.composedPath()[0];
				if(element instanceof HTMLAnchorElement) {
					const path = element.getAttribute('href');
					const route = this.routes.find(path);
					if(route) {
						e.preventDefault();
						this.go(path, PUSH_STATE, path);
					}
				}
			};
			this[_popStateHandler] = e => {
				let state = null;
				try { state = State.fromObject(e.state); }
				catch(e) { /*do nothing*/ }
				if(state)
					this.go(state.path, NO_STATE, state.href);
			};
			this[_current] = null;

			this.routes = routes;
			this.history = history;
			this.document = document;
			this.window = window;
			this.titleAppend = titleAppend;
			
		}
		get routes() {
			return this[_routes];
		}
		set routes(v) {
			if(!(v instanceof Routes))
				throw new TypeError('routes must be an instance of Routes.');
			this[_routes] = v;
		}
		get history() {
			return this[_history];
		}
		set history(v) {
			if(v !== null && !(v instanceof History))
				throw new TypeError('history must be an instance of History.');
			this[_history] = v;
		}
		get current() {
			return this[_current];
		}
		get document() {
			return this[_document];
		}
		set document(v) {
			if(v !== null && !(v instanceof HTMLDocument))
				throw new TypeError('document must be an instance of HTMLDocument.');
			if(this.document)
				this.document.removeEventListener('click', this[_clickHandler]);
			this[_document] = v;
			if(this.document)
				this.document.addEventListener('click', this[_clickHandler]);
		}
		get window() {
			return this[_window];
		}
		set window(v) {
			if(v !== null && !(v instanceof Window))
				throw new TypeError('window must be an instance of Window.');
			if(this.window)
				this.window.removeEventListener('popstate', this[_popStateHandler]);
			this[_window] = v;
			if(this.window)
				this.window.addEventListener('popstate', this[_popStateHandler]);
		}
		get titleAppend() {
			return this[_titleAppend];
		}
		set titleAppend(v) {
			if(typeof v !== 'string')
				throw new TypeError('titleAppend must be a string.');
			this[_titleAppend] = v;
		}
		go(pathname, stateOperation, href) {
			if(typeof pathname !== 'string')
				throw new TypeError('path must be a string.');
			if(Object.values(STATE_OPERATIONS).indexOf(stateOperation) === -1)
				throw new TypeError(`stateOperation must be one of ${Object.keys(STATE_OPERATIONS).join(', ')}.`);
			
			const route = this.routes.find(pathname);
			if(!route)
				throw new Error(`Route for ${pathname} not found.`);
			//do nothing if we're already at this route and we're not attempting to replace state
			if(this.current === route && stateOperation !== REPLACE_STATE)
				return;
			
			//capture the current href
			route.href = href;
			this.document.title = `${route.title ? route.title +' - ' : ''}${this.titleAppend}`;
			this[_current] = route;


			switch(stateOperation) {
				case NO_STATE:
					break;
				case PUSH_STATE:
					this.history.pushState((new State(pathname, href)).toJSON(), '', href);
					break;
				case REPLACE_STATE:
					this.history.replaceState((new State(pathname, href)).toJSON(), '', href);
					break;
			}
				
			this.trigger('update', this.current);
		}
		static fromObject(obj) {
			if(obj === null || typeof obj !== 'object')
				throw new TypeError('obj must be an object.');
			return new this(
				Routes.fromArray(obj.routes || []),
				obj.history || null,
				obj.document || null,
				obj.window || null,
				obj.titleAppend || ''
			);
		}
	}
	return Router;
})();